import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, XIcon } from 'lucide-react';
import { PromptBox } from '../demandes/PromptBox';
import { PhotoUploadBubble } from './PhotoUploadBubble';
import { AtnaSatisfaction } from './AtnaSatisfaction';
import type { ShellTurn } from '../../types/atna';
interface AtnaDialogShellProps {
  /** Shared layout id: the launcher pill morphs into this panel. */
  layoutId: string;
  /** Sous-titre de l'en-tête, ex. « Signaler un problème ». */
  subtitle: string;
  onClose: () => void;
  turns: ShellTurn[];
  thinking: boolean;
  /** Id du dernier tour d'ATNA : seul celui-ci garde ses actions. */
  liveAtnaId?: string;
  renderAtna: (kind: string, live: boolean) => React.ReactNode;
  prompt: {
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
    placeholder: string;
    onAddPhoto?: () => void;
    photoAttached?: boolean;
  };
  footnote: string;
  /** Échange terminé : affiche le mini sondage de satisfaction sous le dernier message. */
  satisfaction?: boolean;
}
const ease = [0.23, 1, 0.32, 1] as const;
export function AtnaDialogShell({
  layoutId,
  subtitle,
  onClose,
  turns,
  thinking,
  liveAtnaId,
  renderAtna,
  prompt,
  footnote,
  satisfaction = false
}: AtnaDialogShellProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // Défile jusqu'au nouveau contenu, sans jamais dépasser le début de la dernière réponse d'ATNA :
  // une longue réponse se lit depuis son début, sans avoir à remonter.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Attend la mise en page (bulles qui apparaissent, boutons retirés de l'ancien message).
    const frame = window.requestAnimationFrame(() => {
      const bottom = el.scrollHeight - el.clientHeight;
      const lastAtna = liveAtnaId ? el.querySelector<HTMLElement>(`[data-turn-id="${liveAtnaId}"]`) : null;
      // Nouvelle réponse : on montre le message de la citoyenne juste au-dessus, puis le début de la réponse.
      const previous = lastAtna?.previousElementSibling as HTMLElement | null;
      const anchor = previous?.dataset.role === 'user' ? previous : lastAtna;
      const target = anchor && !thinking ? Math.min(bottom, anchor.offsetTop - 16) : bottom;
      if (Math.abs(target - el.scrollTop) < 2) return;
      el.scrollTo({
        top: target,
        behavior: turns.length > 1 ? 'smooth' : 'auto'
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [turns.length, thinking, satisfaction, liveAtnaId]);
  return <div className="fixed inset-0 z-[100] flex items-end justify-center p-3 sm:items-center sm:p-6" data-id="element-272" data-mp="1mwz2m1jgvqne">
      <motion.button type="button" aria-label="Fermer la conversation" onClick={onClose} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} className="absolute inset-0 h-full w-full cursor-default bg-ville-navy/40 focus:outline-none" data-id="element-273" data-mp="1hyxg16mu1976" />

      <motion.div layoutId={layoutId} role="dialog" aria-modal="true" aria-label={`Conversation avec ATNA — ${subtitle}`} transition={{
      duration: 0.28,
      ease
    }} style={{
      borderRadius: 28
    }} className="relative flex h-[86vh] w-full max-w-[760px] flex-col overflow-hidden border border-slate-200 bg-white shadow-2xl shadow-ville-navy/20 sm:h-[min(720px,calc(100vh-96px))]" data-id="element-274" data-mp="25pt884v7a6ey">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18,
        ease,
        delay: 0.12
      }} className="flex min-h-0 flex-1 flex-col" data-id="element-275" data-mp="g9tra1lcbcor">
          <header className="flex shrink-0 items-center justify-between gap-4 border-b border-slate-100 px-5 py-4 sm:px-6" data-id="element-276" data-mp="mnh8c8p1itg2">
            <div className="flex items-center gap-3" data-id="element-277" data-mp="vq06ifhvshvz">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" aria-hidden="true" data-id="element-278" data-mp="vop0sa4vl028">
                <SparklesIcon className="h-4 w-4" data-id="element-279" data-mp="32crdfzdqjgq" />
              </span>
              <div data-id="element-280" data-mp="80o01ndxunxd">
                <p className="text-[15px] font-extrabold leading-tight text-ville-navy" data-id="element-281" data-mp="805yqccf5jil">ATNA</p>
                <p className="text-xs text-slate-500" data-id="element-282" data-mp="cre48xamokqh">{subtitle}</p>
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Fermer la conversation" className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-283" data-mp="3bg37qhyuglfc">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-284" data-mp="2yxr0ppcotisc" />
            </button>
          </header>

          <div ref={scrollRef} className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6" data-id="element-285" data-mp="2uvvrlyoim140">
            <ol role="log" aria-live="polite" aria-label="Messages" className="space-y-4" data-id="element-286" data-mp="2pzf4n7t1xssi">
              {turns.map(turn => turn.role === 'user' ? <motion.li key={turn.id} data-turn-id={turn.id} data-role="user" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.2,
              ease
            }} className="flex justify-end" data-id="element-287" data-mp="2g79g6820dti2">
                    {turn.photoUrl ? <PhotoUploadBubble src={turn.photoUrl} fileName="photo-signalement.jpg" size="1,9 Mo" data-id="element-288" data-mp="1sjunla9vs41n" /> : <div className="max-w-[85%] rounded-3xl rounded-br-lg bg-ville-navy px-5 py-3 text-[15px] font-medium leading-relaxed text-white sm:max-w-[75%]" data-id="element-289" data-mp="1wk4sskpumqfg">
                        {turn.text}
                        {turn.withPhoto && <span className="mt-2 block text-xs font-semibold text-white/60" data-id="element-290" data-mp="2bc6nlqd2at8m">Photo jointe</span>}
                      </div>}
                  </motion.li> : <motion.li key={turn.id} data-turn-id={turn.id} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.24,
              ease
            }} className="flex items-start gap-3" data-id="element-291" data-mp="21l2a6v31bgfw">
                    <span className="mt-1 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" aria-hidden="true" data-id="element-292" data-mp="nlh6xcyqom12">
                      <SparklesIcon className="h-4 w-4" data-id="element-293" data-mp="wczo9r7f97vq" />
                    </span>
                    <div className="min-w-0 flex-1 rounded-3xl rounded-bl-lg border border-slate-200 bg-white px-5 py-4" data-id="element-294" data-mp="sbxpfbhf2xua">
                      {renderAtna(turn.kind, turn.id === liveAtnaId && !thinking)}
                    </div>
                  </motion.li>)}

              {thinking && <li className="flex items-center gap-3" data-id="element-295" data-mp="docljfcpwulb">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" aria-hidden="true" data-id="element-296" data-mp="8oq7x4sz9cbz">
                    <SparklesIcon className="h-4 w-4" data-id="element-297" data-mp="1k453q59g9y5i" />
                  </span>
                  <span className="sr-only" data-id="element-298" data-mp="245q7p40c5ksr">ATNA rédige une réponse</span>
                  <span className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-3" data-id="element-299" data-mp="247kt0jfirexd">
                    {[0, 1, 2].map(dot => <motion.span key={dot} className="h-2 w-2 rounded-full bg-slate-300" animate={{
                  opacity: [0.3, 1, 0.3]
                }} transition={{
                  duration: 0.9,
                  repeat: Infinity,
                  delay: dot * 0.15,
                  ease: 'linear'
                }} data-id="element-300" data-mp="2d5d93pt2eqlt" />)}
                  </span>
                </li>}

              {satisfaction && !thinking && <AtnaSatisfaction key={liveAtnaId} data-id="element-301" data-mp="2wskpft5rq62j" />}
            </ol>
          </div>

          <div className="shrink-0 border-t border-slate-100 px-4 pb-4 pt-3 sm:px-5" data-id="element-302" data-mp="30v7ks41ne94h">
            <PromptBox value={prompt.value} onChange={prompt.onChange} onSubmit={prompt.onSubmit} onAddPhoto={prompt.onAddPhoto} variant="chat" autoFocus placeholder={prompt.placeholder} busy={thinking} photoAttached={prompt.photoAttached} data-id="element-303" data-mp="12hywbki05fr7" />
            <p className="mt-2 text-center text-xs text-slate-400" data-id="element-304" data-mp="p9owohvi2xaj">{footnote}</p>
          </div>
        </motion.div>
      </motion.div>
    </div>;
}